// Time & Materials defect capture (slice 1d, Commit C): pure helpers for the station cause picker,
// the Job Entry load hints and the Sales Order defects panel. No React.
//
// Gating rule (ui-plan.md §3): everything here keys on the RECORD — a job's so_pricing_mode, the
// tm_* keys of an add_cut_sheet / add_cut_list answer, a T&M order's tm_defects rows — never on the
// /features flag. A missing / null / 'fixed' value is fixed-price, and a fixed-price record gets
// exactly the behaviour it had before T&M (no extra key in a payload, no extra modal).

import { CAUSE_OPTIONS, isTmLinked } from './tmPricing';

/** A cause code as its neutral label ('handling' -> 'Handling damage'); null/unknown -> fallback. */
export function causeLabel(code, fallback = 'no cause recorded') {
  const opt = CAUSE_OPTIONS.find((c) => c.code === code);
  if (opt) return opt.label;
  return code ? String(code) : fallback;
}

/**
 * The POST /defects body with the picked cause added. A fixed-price defect (no pick) returns the
 * SAME object, so its JSON is byte-identical to the body the page sent before T&M.
 */
export function withCause(payload, pick) {
  if (!pick || !pick.cause_code) return payload;
  return { ...payload, cause_code: pick.cause_code };
}

/**
 * What a GET /queue/{workstation} row says about the job's order. jobq-api (since 2026-09-28) puts
 * the job's HOME sales order's so_pricing_mode on every queue row, with GET /jobs' meaning: 'tm',
 * 'fixed', or null when the job has no home order. true only for 'tm'; false for any other value
 * the row carries (null / 'fixed' = fixed-price, today's prompt). undefined when there is no row
 * (null / not an object: e.g. the desktop's selected job is not among the loaded queue or search
 * rows) or the row has NO so_pricing_mode key (an older API): only then does the caller fall back to
 * its own lookup (mobile: startJobProbe; desktop: the GET /jobs/{id} answer).
 */
export function queueRowSaysTm(row) {
  if (!row || typeof row !== 'object') return undefined;
  if (!Object.prototype.hasOwnProperty.call(row, 'so_pricing_mode')) return undefined;
  return isTmLinked(row);
}

/**
 * The fallback when the queue row does not say (queueRowSaysTm(row) === undefined, an older API):
 * start GET /jobs/{id}.
 * Returns a promise of the job JSON, or null on any failure. It never rejects and has NO timer:
 * the wait limit starts only when the page awaits the answer (awaitJobProbe), so the time the
 * operator spends in the prompts in between does not count against it.
 *
 * A row that says it has no sales order (sales_order_id === null) cannot be T&M: no request.
 */
export function startJobProbe(apiBase, jobId, { salesOrderId } = {}) {
  if (jobId === null || jobId === undefined) return Promise.resolve(null);
  if (salesOrderId === null) return Promise.resolve(null);
  try {
    return fetch(`${apiBase}/jobs/${jobId}`)
      .then((r) => (r && r.ok && typeof r.json === 'function' ? r.json() : null))
      .catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

/**
 * Wait for a started probe, at most timeoutMs FROM NOW. true only when the job's order is T&M; a
 * failure, a missing so_pricing_mode or no answer in time reads as fixed-price (recorded as
 * before T&M). A probe that settled while a native prompt blocked the page wins, because the
 * timer is created only here.
 */
export async function awaitJobProbe(probe, { timeoutMs = 4000 } = {}) {
  let timer = null;
  try {
    const late = new Promise((res) => { timer = setTimeout(() => res(null), timeoutMs); });
    // A settled probe wins over a timer that fires at once (no extra promise hop before the race).
    const job = await Promise.race([probe, late]).catch(() => null);
    return isTmLinked(job);
  } catch (e) {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** start + await in one step (the wait limit starts at the call). */
export async function probeJobIsTm(apiBase, jobId, { timeoutMs = 4000, salesOrderId } = {}) {
  return awaitJobProbe(startJobProbe(apiBase, jobId, { salesOrderId }), { timeoutMs });
}

// ─── Job Entry: the load hints of add_cut_sheet / add_cut_list ──────────────────────────────────

/** A response body as JSON, or null (no body, not JSON, a test double without json()): never throws. */
export async function readJsonQuietly(res) {
  try {
    if (!res || typeof res.json !== 'function') return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}

/**
 * The T&M hints of ONE job-load answer, or null when there is nothing to show (a fixed-price job's
 * answer has no tm_* key; a T&M job with no overrun and no remake question has empty lists).
 * {job_id, master_job_num, warnings, prompts, hintsError}
 */
export function loadHintsFrom(addJson, job) {
  if (!addJson || typeof addJson !== 'object') return null;
  const warnings = Array.isArray(addJson.tm_warnings) ? addJson.tm_warnings : [];
  const prompts = (Array.isArray(addJson.tm_remake_prompt) ? addJson.tm_remake_prompt : [])
    .filter((p) => p && Number(p.suggested_qty) > 0 && p.defect_id != null);
  const hintsError = addJson.tm_hints_error ? String(addJson.tm_hints_error) : null;
  if (warnings.length === 0 && prompts.length === 0 && !hintsError) return null;
  return {
    job_id: job ? job.job_id : null,
    master_job_num: job ? job.master_job_num : null,
    warnings,
    prompts,
    hintsError,
  };
}

/** The most pieces a "remake of defect #X?" answer may link: suggested_qty, never above open_qty. */
export function promptMaxQty(p) {
  const s = Math.floor(Number(p && p.suggested_qty) || 0);
  const open = p && p.open_qty != null ? Math.floor(Number(p.open_qty) || 0) : s;
  return Math.max(0, Math.min(s, open));
}

/** The question for one remake prompt: the API's own sentence, else the same words built here. */
export function promptQuestion(p) {
  if (p && typeof p.message === 'string' && p.message.trim()) return p.message.trim();
  const open = p && p.open_qty != null ? p.open_qty : (p && p.n);
  return `Is this a remake of defect #${p && p.defect_id} (${open} pcs at ${(p && p.station) || 'an unknown station'})?`;
}

/** The POST tm_remake_links body for one checked prompt (source 'job_entry'; ui-plan.md §1.7). */
export function remakeLinkBody(p, qty, pin) {
  const body = { defect_id: p.defect_id, makeup_job_id: p.job_id, qty, source: 'job_entry', pin };
  if (p.part_id !== null && p.part_id !== undefined) body.part_id = p.part_id;
  return body;
}

// ─── Sales Order: decisions ─────────────────────────────────────────────────────────────────────

/**
 * One defect's billing decision from GET tm_defects: charged by default (decision 1); a live
 * not_charged decision = shop error. {shopError, decided, label, by, at, why}
 */
export function defectDecision(d) {
  const live = (d && d.decision) || null;
  const shopError = !!(live && live.not_charged);
  return {
    shopError,
    decided: !!live,
    label: shopError ? 'Shop error: not charged' : 'Charged',
    by: live ? (live.decided_by || null) : null,
    at: live ? (live.decided_at || null) : null,
    why: live ? (live.reason || null) : null,
  };
}

/**
 * Whether the SO panel offers "Charged or shop error…" on one tm_defects row: only where the API
 * would accept a decision (main.py _tm_defect_unpriced_refusal refuses a defect on a Cancelled /
 * Voided job, a part defect naming no part, a part that is not a line, a sheet on no line's nest).
 * Un-marking a live shop-error mark is always allowed (the API skips the priced check for it).
 * The line checks use the collector's view, so they apply only when actuals_available.
 */
export function canDecideDefect(d, data) {
  if (!d) return false;
  if (defectDecision(d).shopError) return true;
  if (d.job_live === false) return false;
  const sheet = d.defect_type === 'sheet';
  if (!sheet && (d.part_id === null || d.part_id === undefined)) return false;
  if (data && data.actuals_available) {
    if (sheet) return Array.isArray(d.sheet_lines) && d.sheet_lines.length > 0;
    return d.so_line_id !== null && d.so_line_id !== undefined;
  }
  return true;
}

/** defect_id -> defectDecision, for the decision chip on the production deep dive's defect rows. */
export function decisionsByDefectId(data) {
  const out = new Map();
  const list = data && Array.isArray(data.defects) ? data.defects : [];
  list.forEach((d) => { if (d && d.defect_id != null) out.set(d.defect_id, defectDecision(d)); });
  return out;
}

/**
 * An API timestamp as 'YYYY-MM-DD HH:MM' in the viewer's LOCAL time (the page's other times are
 * local too). The API serializes with str(): '2026-09-25 13:10:00.123456+00:00'. Its DB session runs
 * in UTC, so a value without an offset is UTC as well. A date alone, or a value that does not parse,
 * is shown as sent (first 16 characters).
 */
export function stamp(ts) {
  if (!ts) return '';
  const raw = String(ts).trim();
  if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw)) return raw.slice(0, 16).replace('T', ' ');
  let iso = raw.replace(' ', 'T');
  if (!/(Z|[+-]\d{2}(:?\d{2})?)$/i.test(iso)) iso += 'Z';
  else if (/[+-]\d{2}$/.test(iso)) iso += ':00';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 16).replace('T', ' ');
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** {job_id: master_job_num} from a truth payload's related_documents.jobs (labels only). */
export function jobNumsFrom(related) {
  const out = {};
  const jobs = related && Array.isArray(related.jobs) ? related.jobs : [];
  jobs.forEach((j) => { if (j && j.job_id != null && j.master_job_num != null) out[j.job_id] = j.master_job_num; });
  return out;
}
